import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Download, ChevronLeft, ChevronRight, Shield, Clock, User, Globe, FileText } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABELS = {
  user_login: { label: "Login", color: "bg-green-100 text-green-800" },
  login_failed: { label: "Login Failed", color: "bg-red-100 text-red-800" },
  user_logout: { label: "Logout", color: "bg-slate-100 text-slate-800" },
  ticket_created: { label: "Ticket Created", color: "bg-blue-100 text-blue-800" },
  ticket_updated: { label: "Ticket Updated", color: "bg-yellow-100 text-yellow-800" },
  ticket_deleted: { label: "Ticket Deleted", color: "bg-red-100 text-red-800" },
  comment_added: { label: "Comment Added", color: "bg-purple-100 text-purple-800" },
  hr_request_created: { label: "HR Request Created", color: "bg-blue-100 text-blue-800" },
  hr_request_updated: { label: "HR Request Updated", color: "bg-yellow-100 text-yellow-800" },
  user_updated: { label: "User Updated", color: "bg-orange-100 text-orange-800" },
  user_deleted: { label: "User Deleted", color: "bg-red-100 text-red-800" },
  settings_updated: { label: "Settings Updated", color: "bg-indigo-100 text-indigo-800" },
  audit_logs_exported: { label: "Logs Exported", color: "bg-slate-100 text-slate-700" },
};

const ITEMS_PER_PAGE = 50;

export default function AuditLogs({ user }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [actions, setActions] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("skip", String((currentPage - 1) * ITEMS_PER_PAGE));
      params.set("limit", String(ITEMS_PER_PAGE));
      if (actionFilter && actionFilter !== "all") params.set("action", actionFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);

      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (e) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [currentPage, actionFilter, debouncedSearch, startDate, endDate]);

  const fetchActions = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/actions`);
      setActions(response.data);
    } catch (e) { /* non-critical */ }
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [actionFilter, startDate, endDate]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (actionFilter && actionFilter !== "all") params.set("action", actionFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);

      const response = await axios.get(`${API}/audit-logs/export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audit_logs_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Audit logs exported");
    } catch (e) {
      toast.error("Failed to export logs");
    }
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, total);

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString();
  };

  const getActionInfo = (action) => {
    return ACTION_LABELS[action] || { label: action.replace(/_/g, " "), color: "bg-slate-100 text-slate-700" };
  };

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;
    return Object.entries(details).map(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        return (
          <div key={key} className="text-xs text-slate-600 dark:text-slate-400">
            <span className="font-medium">{key.replace(/_/g, " ")}:</span>{" "}
            {Object.entries(value)
              .filter(([, v]) => v !== null && v !== undefined)
              .map(([k, v]) => `${k}: ${v}`)
              .join(", ")}
          </div>
        );
      }
      return (
        <div key={key} className="text-xs text-slate-600 dark:text-slate-400">
          <span className="font-medium">{key.replace(/_/g, " ")}:</span> {String(value)}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div>
        <Navbar user={user} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#1E1E1E]">
      <Navbar user={user} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white" data-testid="audit-logs-title">Audit Logs</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{total} events recorded</p>
            </div>
          </div>
          <Button onClick={handleExport} variant="outline" data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-0 shadow-md">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, ticket..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="audit-search-input"
                />
              </div>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="audit-action-filter">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actions.map((a) => (
                    <SelectItem key={a} value={a}>
                      {getActionInfo(a).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                data-testid="audit-start-date"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                data-testid="audit-end-date"
              />
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="border-0 shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-logs-table">
              <thead className="bg-slate-50 dark:bg-[#343434] border-b border-slate-200 dark:border-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">
                    <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> Timestamp</div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">
                    <div className="flex items-center gap-1"><User className="w-3 h-3" /> User</div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">
                    <div className="flex items-center gap-1"><Globe className="w-3 h-3" /> IP Address</div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-white uppercase">
                    <div className="flex items-center gap-1"><FileText className="w-3 h-3" /> Details</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-600">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-slate-500 dark:text-white">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const actionInfo = getActionInfo(log.action);
                    const isExpanded = expandedLog === log.id;
                    return (
                      <tr
                        key={log.id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                        onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                        data-testid={`audit-log-${log.id}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700 dark:text-white">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge className={actionInfo.color}>{actionInfo.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-800 dark:text-white">{log.user_name || "System"}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{log.user_email || ""}</div>
                          {log.user_role && (
                            <div className="text-xs text-slate-400 dark:text-slate-500">{log.user_role.replace(/_/g, " ")}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300 font-mono">
                          {log.ip_address || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {isExpanded ? (
                            <div className="space-y-1">
                              {formatDetails(log.details)}
                              {log.user_agent && (
                                <div className="text-xs text-slate-400 dark:text-slate-500 mt-2 break-all">
                                  <span className="font-medium">Browser:</span> {log.user_agent.substring(0, 120)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-slate-600 dark:text-slate-400 truncate max-w-xs">
                              {log.details && Object.keys(log.details).length > 0
                                ? Object.entries(log.details)
                                    .filter(([, v]) => v !== null && typeof v !== "object")
                                    .map(([k, v]) => `${k}: ${v}`)
                                    .join(", ")
                                    .substring(0, 60) || "Click to expand"
                                : "—"}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pagination */}
        {total > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between mt-6 px-2" data-testid="audit-pagination">
            <div className="text-sm text-slate-600 dark:text-gray-400">
              Showing {startIndex + 1}-{endIndex} of {total} events
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
